import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ResultadoFacturaSiigo } from '@manejo-alcanza/contracts';
import { estadoSiigo, importarDeSiigo, pesos } from '../api';
import { Aviso, Boton, Card, Error_ } from './basicos';

/**
 * Traer de Siigo las facturas ya emitidas.
 *
 * **Solo lee.** Escribir en Siigo está aparcado a propósito: una factura mal
 * emitida no es un bug, es un problema tributario.
 *
 * El resultado sale **por factura, no como un número**. «Se importaron 12» no
 * dice cuáles faltaron ni por qué, y obliga a ir a buscarlo a mano — que es
 * justo lo que este sistema existe para evitar.
 */
export function ImportarSiigo(): ReactNode {
  const cliente = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [desde, setDesde] = useState(haceUnMes());
  const [hasta, setHasta] = useState(hoy());

  const estado = useQuery({ queryKey: ['siigo-estado'], queryFn: estadoSiigo });

  const importar = useMutation({
    mutationFn: () => importarDeSiigo(desde, hasta),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['facturas'] }),
  });

  // Mientras no se sepa si está configurada, no se ofrece nada: un botón que
  // falla al pulsarlo es peor que un botón que no está.
  if (estado.isLoading || estado.isError) return null;

  if (!estado.data!.configurada) {
    return (
      <Card titulo="Traer facturas de Siigo">
        <p className="text-sm text-tenue">
          La integración no está configurada. Faltan estas variables de entorno:{' '}
          {estado.data!.faltan.map((v) => (
            <code key={v} className="mx-1 rounded bg-fondo px-1 font-mono text-xs">
              {v}
            </code>
          ))}
        </p>
        <p className="mt-2 text-xs text-tenue">
          En producción se ponen con <code className="font-mono">fly secrets set</code>. Mientras
          tanto, las facturas se registran a mano y todo lo demás funciona igual.
        </p>
      </Card>
    );
  }

  const r = importar.data;

  return (
    <Card titulo="Traer facturas de Siigo">
      {!abierto && !r && (
        <>
          <p className="mb-3 text-sm text-tenue">
            Trae las facturas <strong>ya emitidas</strong> en Siigo para poder conciliarlas. No
            emite nada: el sistema no factura.
          </p>
          <Boton onClick={() => setAbierto(true)}>Elegir el rango</Boton>
        </>
      )}

      {abierto && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="mb-1 block text-xs text-tenue">Desde</span>
              <input
                type="date"
                value={desde}
                max={hasta}
                onChange={(e) => setDesde(e.target.value)}
                className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-tenue">Hasta</span>
              <input
                type="date"
                value={hasta}
                max={hoy()}
                min={desde}
                onChange={(e) => setHasta(e.target.value)}
                className="rounded-md border border-borde bg-superficie px-2 py-1.5 text-sm"
              />
            </label>
            <Boton onClick={() => importar.mutate()} deshabilitado={importar.isPending}>
              {importar.isPending ? 'Trayendo…' : 'Traer'}
            </Boton>
            <Boton tipo="secundario" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
          </div>
          {/* Traer el mismo rango dos veces no duplica nada: la importación es
              idempotente, igual que la carga de órdenes. */}
          <p className="text-xs text-tenue">
            Traer el mismo rango dos veces no duplica nada.
          </p>
        </div>
      )}

      {importar.isError && (
        <div className="mt-3">
          <Error_>{(importar.error as Error).message}</Error_>
        </div>
      )}

      {r && <Resultado resultado={r} alRepetir={() => importar.reset()} />}
    </Card>
  );
}

// ---------------------------------------------------------------------------

function Resultado({
  resultado,
  alRepetir,
}: {
  resultado: {
    revisadas: number;
    enlazadasSolas: number;
    seAlcanzoElTope: boolean;
    resultados: ResultadoFacturaSiigo[];
  };
  alRepetir: () => void;
}): ReactNode {
  const por = (d: ResultadoFacturaSiigo['desenlace']): ResultadoFacturaSiigo[] =>
    resultado.resultados.filter((x) => x.desenlace === d);

  const importadas = por('IMPORTADA');
  const enlazadas = por('ENLAZADA');
  const yaEstaban = por('YA_EXISTIA');
  const cambiaron = por('CAMBIO_EN_SIIGO');
  const omitidas = por('OMITIDA');
  const rechazadas = por('RECHAZADA');
  const fallaron = por('FALLO');

  return (
    <div className="mt-4 space-y-4 border-t border-borde pt-4">
      <p className="text-sm">
        Siigo devolvió <strong>{resultado.revisadas}</strong> facturas.
      </p>

      {/*
        Antes se cortaba en silencio y la frase de arriba se leía como «eso es
        todo». En esta cuenta hay más de tres mil facturas: pedir un año entero
        daba exactamente mil y la impresión de que ya estaban todas.
      */}
      {resultado.seAlcanzoElTope && (
        <Aviso>
          <strong>No son todas.</strong> La importación se paró en el tope de 1.000 por
          tanda, así que pueden faltar facturas de ese rango. Vuelve a traer con un rango
          más corto — por meses, por ejemplo— hasta que este aviso no salga. Traer dos veces
          lo mismo no duplica nada.
        </Aviso>
      )}

      {importadas.length > 0 && (
        <Grupo titulo={`Entraron (${importadas.length})`}>
          {importadas.map((f) => (
            <li key={f.referenciaSiigo} className="flex flex-wrap items-baseline justify-between gap-2">
              <span>
                {'facturaId' in f && (
                  <Link to={`/plata/${f.facturaId}`} className="font-medium text-acento hover:underline">
                    {f.numero}
                  </Link>
                )}
                {'clienteNombre' in f && <span className="text-tenue"> · {f.clienteNombre}</span>}
              </span>
              <span className="cifra">{'valorTotal' in f ? pesos(f.valorTotal) : ''}</span>
            </li>
          ))}
        </Grupo>
      )}

      {/*
        Lo que más vale del resultado: entraron, ¿pero ya sirven para comparar?
        Sin orden enlazada no hay facturado contra recibido.
      */}
      {resultado.enlazadasSolas > 0 && (
        <p className="text-sm">
          <strong>{resultado.enlazadasSolas}</strong> quedaron enlazadas con su orden sin que
          nadie tuviera que decidir nada: las observaciones de Siigo decían el número y el
          valor cuadraba.
        </p>
      )}

      {importadas.length > 0 && (
        <Aviso>
          <strong>Las que quedaron sin orden están en Conciliación.</strong> Siigo no sabe a
          qué orden corresponde una factura — nadie lo sabe salvo quien la emitió. Allí el
          sistema ya propone cuál es y por qué; hasta que se enlacen, no se puede comparar lo
          facturado contra lo que el cliente recibió.{' '}
          <Link to="/plata?ver=conciliacion" className="text-acento hover:underline">
            Ir a conciliar
          </Link>
          .
        </Aviso>
      )}

      {enlazadas.length > 0 && (
        <Grupo titulo={`Se enlazaron con las que ya estaban (${enlazadas.length})`}>
          {enlazadas.map((f) => (
            <li key={f.referenciaSiigo}>
              {'facturaId' in f && (
                <Link to={`/plata/${f.facturaId}`} className="font-medium text-acento hover:underline">
                  {f.numero}
                </Link>
              )}
              {'coincideElValor' in f && !f.coincideElValor && (
                <span className="ml-2 text-xs text-alerta">
                  el valor no cuadra: Siigo dice {pesos(f.valorEnSiigo)} y aquí estaba{' '}
                  {pesos(f.valorRegistrado)}
                </span>
              )}
            </li>
          ))}
        </Grupo>
      )}

      {/*
        Lo que más hay que ver del resultado: una factura que ya teníamos y que
        en Siigo dice otra cosa. No se sobrescribe — Siigo es la verdad fiscal y
        gana, pero gana como gana el PDF sobre el JSON: la discrepancia se
        reporta, no se resuelve a escondidas.
      */}
      {cambiaron.length > 0 && (
        <Grupo titulo={`Cambiaron en Siigo (${cambiaron.length})`}>
          {cambiaron.map((f) => (
            <li key={f.referenciaSiigo} className="border-l-2 border-aviso pl-3">
              {'facturaId' in f && (
                <Link to={`/plata/${f.facturaId}`} className="font-medium text-acento hover:underline">
                  {f.numero}
                </Link>
              )}
              {'cambios' in f && (
                <ul className="mt-0.5 text-xs text-aviso">
                  {f.cambios.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          <p className="mt-2 text-xs text-tenue">
            No se tocó nada aquí. Siigo es la verdad fiscal, pero qué hacer con la diferencia
            lo decide una persona.
          </p>
        </Grupo>
      )}

      {yaEstaban.length > 0 && (
        <p className="text-sm text-tenue">
          {yaEstaban.length} ya se habían traído antes y no se duplicaron.
        </p>
      )}

      {omitidas.length > 0 && (
        <Grupo titulo={`Omitidas (${omitidas.length})`}>
          {omitidas.map((f) => (
            <li key={f.referenciaSiigo} className="text-tenue">
              <span className="font-medium">{f.numero}</span> — {'motivo' in f && f.motivo}
            </li>
          ))}
          <p className="mt-2 text-xs text-tenue">
            Son de clientes que no están en el maestro. Es lo normal: Siigo lleva toda la
            facturación de la empresa, y aquí solo interesa el retail.
          </p>
        </Grupo>
      )}

      {rechazadas.length > 0 && (
        <Grupo titulo={`No se pudieron leer (${rechazadas.length})`}>
          {rechazadas.map((f) => (
            <li key={f.referenciaSiigo} className="text-alerta">
              <span className="font-medium">{f.numero ?? f.referenciaSiigo}</span> —{' '}
              {'motivo' in f && f.motivo}
            </li>
          ))}
        </Grupo>
      )}

      {/*
        «No se pudo leer» y «no se pudo guardar» son dos problemas distintos y se
        arreglan en sitios distintos, asi que se cuentan aparte. Antes esto ni se
        veia: una factura que no se podia guardar tumbaba el recorrido entero y la
        pantalla solo decia «algo fallo de nuestro lado».
      */}
      {fallaron.length > 0 && (
        <Grupo titulo={`No se pudieron guardar (${fallaron.length})`}>
          {fallaron.map((f) => (
            <li key={f.referenciaSiigo} className="text-alerta">
              <span className="font-medium">{f.numero ?? f.referenciaSiigo}</span> —{' '}
              {'motivo' in f && f.motivo}
            </li>
          ))}
          <p className="mt-2 text-xs text-tenue">
            Siigo las mandó bien; el problema fue al guardarlas aquí. Las demás sí
            entraron. Cuando esté arreglado, trae el mismo rango otra vez: no se duplica
            nada.
          </p>
        </Grupo>
      )}
      <Boton tipo="secundario" onClick={alRepetir}>
        Traer otro rango
      </Boton>
    </div>
  );
}

function Grupo({ titulo, children }: { titulo: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold">{titulo}</h3>
      <ul className="space-y-1 text-sm">{children}</ul>
    </div>
  );
}

function hoy(): string {
  return new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function haceUnMes(): string {
  return new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
